import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useMethodologyQueryActions } from '@/api/hooks/methodologyQueryActions';
import {
  EstimationModelItemGroupLevel,
  type EstimationModelResponse,
} from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import type { CustomDropdownOption } from '@/components/atoms/CustomDropdown';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { Loader } from '@/components/atoms/Loader';
import { TextFormField } from '@/components/atoms/TextFormField';
import { UserAutocompleteFormField } from '@/components/atoms/UserAutocompleteFormField';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { ModalStateless } from '@/components/ModalStateless';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useAuth } from '@/context/auth';
import { useMessages } from '@/hooks/messages';
import { sortExpressions } from '@/utils/sorting';

import { customEstimationItemCreationModalMessageDefinitions } from './messages';

const NO_PARENT_VALUE = '__none__';

interface CustomEstimationItemCreationFormModel {
  name: string;
  groupId: string | null;
  parentId: string;
  phaseLeadId: string | null;
  methodologyId: string | null;
  description: string;
}

export interface CustomEstimationItemCreationSubmitModel {
  name: string;
  groupId: string | null;
  parentId: string | null;
  phaseLeadId: string | null;
  methodologyId: string | null;
  description: string;
}

interface CustomEstimationItemCreationModalProps {
  estimationModel: EstimationModelResponse;
  defaultMethodologyId: string;
  canChangeMethodology: boolean;
  defaultGroupId?: string;
  estimationCultureName: string;
  open: boolean;
  parentItemOptions?: CustomDropdownOption[];
  onSave: (formModel: CustomEstimationItemCreationSubmitModel) => void;
  onClose: () => void;
}

export const CustomEstimationItemCreationModal: FC<CustomEstimationItemCreationModalProps> = ({
  estimationModel,
  defaultMethodologyId,
  canChangeMethodology,
  defaultGroupId,
  estimationCultureName,
  open,
  parentItemOptions,
  onClose,
  onSave,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(customEstimationItemCreationModalMessageDefinitions);

  const { user } = useAuth();

  const { useGetMethodologies } = useMethodologyQueryActions();
  const { data: methodologies, isLoading: isLoadingMethodologies } = useGetMethodologies(
    estimationCultureName,
    {
      methodologyIds: estimationModel?.methodologyIds,
      onlyCurrent: false,
    },
    !!estimationModel,
  );

  const formModelInitial = useMemo<CustomEstimationItemCreationFormModel>(
    () => ({
      name: '',
      description: '',
      groupId: defaultGroupId ?? null,
      parentId: NO_PARENT_VALUE,
      methodologyId: defaultMethodologyId,
      phaseLeadId: user?.id ?? null,
    }),
    [defaultMethodologyId, defaultGroupId, user?.id],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CustomEstimationItemCreationFormModel>({
    defaultValues: formModelInitial,
    mode: 'onChange',
  });

  // EWM-602: the group dropdown is narrowed only for the top-level case when the
  // caller opts into the Parent-dropdown UX (by passing parentItemOptions). The
  // original bug was picking a sub-phase group with no parent, which orphaned the
  // item — so at top level we exclude sub-phase groups and show phase-level groups
  // only. When a parent IS chosen the item can no longer be orphaned, and the
  // backend (EstimationItemService.ValidateGroup) only forbids a *sub-phase* group
  // *without* a parent — it allows any group level once a parent exists. We
  // therefore must NOT restrict children to sub-phase-only: most models have no
  // sub-phase-level groups at all (the Level column defaulted every existing group
  // to Phase), so a sub-phase-only filter left the dropdown empty and made the
  // form submit a null groupId ("Group must be chosen"). Callers that do not pass
  // parentItemOptions keep the unfiltered behaviour (they set the parentId in onSave).
  const selectedParentId = useWatch({ control, name: 'parentId' });
  const filterByLevel = parentItemOptions !== undefined;
  const isCreatingChild = filterByLevel && selectedParentId !== NO_PARENT_VALUE;

  const estimationGroupsOptions = useMemo<CustomDropdownOption[]>(() => {
    if (!estimationModel?.itemGroups) {
      return [];
    }
    const baseGroups = estimationModel.itemGroups.filter((x) => !x.forbidCustomEstimationItem);
    const filteredGroups =
      filterByLevel && !isCreatingChild
        ? baseGroups.filter((x) => x.level === EstimationModelItemGroupLevel.Phase)
        : baseGroups;
    return filteredGroups.sort(sortExpressions.byOrder).map((itemGroup) => ({
      value: itemGroup.id,
      label: itemGroup.name,
    }));
  }, [estimationModel, filterByLevel, isCreatingChild]);

  const parentDropdownOptions = useMemo<CustomDropdownOption[] | undefined>(() => {
    if (!parentItemOptions || parentItemOptions.length === 0) {
      return undefined;
    }
    return [{ value: NO_PARENT_VALUE, label: messages.parentNone() }, ...parentItemOptions];
  }, [parentItemOptions, messages]);

  // If the preselected default group is filtered out by the new level rule,
  // fall back to the first valid option so the form does not submit with a
  // group that doesn't exist in the dropdown.
  useEffect(() => {
    if (!open) {
      return;
    }
    const hasDefault = estimationGroupsOptions.some((option) => option.value === defaultGroupId);
    if (!hasDefault) {
      setValue('groupId', estimationGroupsOptions[0]?.value ?? null);
    }
  }, [open, estimationGroupsOptions, defaultGroupId, setValue]);

  const methodologyOptions = useMemo<CustomDropdownOption[]>(
    () =>
      methodologies?.sort(sortExpressions.byOrder).map((x) => ({
        label: x.name,
        value: x.id,
      })) ?? [],
    [methodologies],
  );

  useEffect(() => {
    reset(formModelInitial);
  }, [open, reset, formModelInitial]);

  if (isLoadingMethodologies) {
    return <Loader />;
  }

  if (!estimationModel || !methodologies || !user) {
    throw new Error('Data not found');
  }

  const submit = (formModel: CustomEstimationItemCreationFormModel) =>
    onSave({
      ...formModel,
      parentId: formModel.parentId === NO_PARENT_VALUE ? null : formModel.parentId,
    });

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={onClose}
      size='s'
      {...{ 'data-testid': 'create-custom-phase-modal' }}
      buttons={
        <>
          <Button
            variant='secondary'
            outlined
            onClick={onClose}
            data-testid='create-custom-phase-cancel-button'
          >
            {globalMessages.cancel()}
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit(submit)}
            data-testid='create-custom-phase-create-button'
          >
            {globalMessages.create()}
          </Button>
        </>
      }
    >
      <form className='space-y-5' data-testid='create-custom-phase-form'>
        <div className={errors.name ? 'has-error' : undefined}>
          <label className='form-label' htmlFor='name'>
            {messages.phaseName()}
            <span className='text-danger'> *</span>
          </label>
          <TextFormField
            control={control}
            fieldPath='name'
            rules={{
              required: true,
              maxLength: entityFieldSettings.estimationItemEntity.nameMaxLength,
            }}
            autoFocus
          />
        </div>
        {parentDropdownOptions && (
          <div>
            <label htmlFor='parentId'>{messages.parent()}</label>
            <DropdownFormField
              control={control}
              fieldPath='parentId'
              options={parentDropdownOptions}
              fullWidth
              data-testid='create-custom-phase-parent-dropdown'
            />
          </div>
        )}
        <div className={errors.groupId ? 'has-error' : undefined}>
          <label className='form-label' htmlFor='groupId'>
            {messages.estimationGroup()}
            <span className='text-danger'> *</span>
          </label>
          <DropdownFormField
            control={control}
            fieldPath='groupId'
            options={estimationGroupsOptions}
            rules={{ required: true }}
            fullWidth
            data-testid='create-custom-phase-group-dropdown'
          />
          {estimationGroupsOptions.length === 0 && (
            <p className='text-danger text-sm'>{messages.noEstimationGroupsAvailable()}</p>
          )}
        </div>
        <div>
          <label htmlFor='phaseLeadId'>{messages.phaseLead()}</label>
          <UserAutocompleteFormField control={control} fieldPath='phaseLeadId' />
        </div>
        <div className={errors.methodologyId ? 'has-error' : undefined}>
          <label className='form-label' htmlFor='methodologyId'>
            {messages.methodology()}
            <span className='text-danger'> *</span>
          </label>
          <DropdownFormField
            control={control}
            fieldPath='methodologyId'
            options={methodologyOptions}
            rules={{ required: true }}
            fullWidth
            disabled={!canChangeMethodology}
          />
        </div>
        <div>
          <label htmlFor='description'>{messages.description()}</label>
          <WysiwygFormTextField
            control={control}
            fieldPath='description'
            placeholder={messages.descriptionPlaceholder()}
            rules={{
              maxLength: entityFieldSettings.estimationItemEntity.descriptionMaxLength,
            }}
          />
        </div>
      </form>
    </ModalStateless>
  );
};
